import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  FaCopy,
  FaExpand,
  FaCompress,
  FaCheck,
  FaExclamationTriangle,
  FaQuestionCircle,
  FaDatabase,
  FaSearch,
  FaCode,
  FaTimes,
  FaChevronDown,
  FaChevronRight,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../../../services/api';

const HELPER_CATEGORIES = [
  {
    name: 'Data',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    helpers: [
      { label: 'Format Number', syntax: '{{formatNumber value}}', desc: 'Locale-formatted number' },
      {
        label: 'Format Date',
        syntax: '{{formatDate value "full"}}',
        desc: 'Formatted date output',
      },
      { label: 'Truncate', syntax: '{{truncate text 50}}', desc: 'Truncate text to length' },
    ],
  },
  {
    name: 'Logic',
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    helpers: [
      { label: '#if', syntax: '{{#if condition}}\n  ...\n{{/if}}', desc: 'Conditional block' },
      {
        label: '#each',
        syntax: '{{#each data}}\n  {{this.[Field Name]}}\n{{/each}}',
        desc: 'Iterate array',
      },
      {
        label: '#unless',
        syntax: '{{#unless condition}}\n  ...\n{{/unless}}',
        desc: 'Negative conditional',
      },
    ],
  },
  {
    name: 'Comparison',
    color: 'text-violet-600',
    bg: 'bg-violet-50',
    border: 'border-violet-200',
    helpers: [
      {
        label: '#ifEquals',
        syntax: '{{#ifEquals a b}}\n  ...\n{{/ifEquals}}',
        desc: 'Equal comparison',
      },
      {
        label: '#ifGreater',
        syntax: '{{#ifGreater a b}}\n  ...\n{{/ifGreater}}',
        desc: 'Greater than',
      },
      { label: '#ifLess', syntax: '{{#ifLess a b}}\n  ...\n{{/ifLess}}', desc: 'Less than' },
    ],
  },
  {
    name: 'Format',
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    helpers: [
      { label: 'Bold', syntax: '{{bold text}}', desc: 'Bold text' },
      { label: 'Italic', syntax: '{{italic text}}', desc: 'Italic text' },
      { label: 'Code', syntax: '{{code text}}', desc: 'Monospace text' },
    ],
  },
  {
    name: 'Layout',
    color: 'text-rose-600',
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    helpers: [
      { label: 'Table', syntax: '{{table data columns}}', desc: 'Data table' },
      { label: 'Chart', syntax: '{{chart "bar" data}}', desc: 'Inline chart' },
      {
        label: '#drilldown',
        syntax: '{{#drilldown "section"}}\n  ...\n{{/drilldown}}',
        desc: 'Collapsible section',
      },
    ],
  },
  {
    name: 'Meta',
    color: 'text-gray-600 dark:text-gray-400',
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    helpers: [
      { label: 'Row Count', syntax: '{{meta.rowCount}}', desc: 'Number of result rows' },
      { label: 'Generated At', syntax: '{{meta.generatedAt}}', desc: 'Generation timestamp' },
      { label: 'Template Name', syntax: '{{meta.templateName}}', desc: 'Current template name' },
    ],
  },
  {
    name: 'Query',
    color: 'text-primary-600',
    bg: 'bg-primary-50',
    border: 'border-primary-200',
    helpers: [
      {
        label: 'Query Inline',
        syntax: '{{query "query-name"}}',
        desc: 'Insert query results inline',
      },
      {
        label: 'Query Block',
        syntax:
          '{{#query "query-name"}}\n  {{#each this}}\n    {{this.[Field Name]}}\n  {{/each}}\n{{/query}}',
        desc: 'Iterate query results',
      },
      { label: 'Query Count', syntax: '{{queryCount "query-name"}}', desc: 'Result count' },
      {
        label: 'If Has Results',
        syntax: '{{#ifQueryHasResults "query-name"}}\n  ...\n{{/ifQueryHasResults}}',
        desc: 'Conditional on results',
      },
    ],
  },
];

const highlightHandlebars = (code) => {
  if (!code) return '';
  let html = code.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  // Comments: {{!-- ... --}} and {{! ... }}
  html = html.replace(
    /(\{\{!--[\s\S]*?--\}\})/g,
    '<span style="color:#5c6370;font-style:italic">$1</span>'
  );
  html = html.replace(
    /(\{\{![\s\S]*?\}\})/g,
    '<span style="color:#5c6370;font-style:italic">$1</span>'
  );

  // Block helpers: {{#if}}, {{#each}}, {{#drilldown}}, {{/...}}, {{else}}
  html = html.replace(
    /(\{\{[#/](?:if|each|unless|ifEquals|ifGreater|ifLess|drilldown|query|ifQueryHasResults)[^}]*\}\})/g,
    '<span style="color:#61afef;font-weight:500">$1</span>'
  );
  html = html.replace(/(\{\{else\}\})/g, '<span style="color:#61afef;font-weight:500">$1</span>');

  // Remaining expressions: {{...}}
  html = html.replace(/(\{\{(?![#/!])(?:(?!\}\}).)*\}\})/g, (match) => {
    // Check if already wrapped in a span (already highlighted)
    if (match.includes('style=')) return match;
    return `<span style="color:#c678dd">${match}</span>`;
  });

  return html;
};

const TemplateEditor = ({
  // Support both prop interfaces
  content: propContent,
  onChange: propOnChange,
  readOnly = false,
  // Legacy props from index.js
  template,
  onValidate,
  sidebarMode = false,
}) => {
  const textareaRef = useRef(null);
  const highlightRef = useRef(null);
  const lineNumberRef = useRef(null);

  // Determine content source
  const initialContent = propContent !== undefined ? propContent : template?.template_content || '';
  const [editorContent, setEditorContent] = useState(initialContent);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showPalette, setShowPalette] = useState(!readOnly);
  const [expandedCategory, setExpandedCategory] = useState('Data');
  const [validationResult, setValidationResult] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [showQueryBrowser, setShowQueryBrowser] = useState(false);
  const [availableQueries, setAvailableQueries] = useState([]);
  const [querySearchTerm, setQuerySearchTerm] = useState('');
  const [loadingQueries, setLoadingQueries] = useState(false);
  const [paletteTab, setPaletteTab] = useState('helpers'); // helpers | queries

  useEffect(() => {
    if (propContent !== undefined) {
      setEditorContent(propContent);
    } else if (template) {
      setEditorContent(template.template_content || '');
    }
  }, [propContent, template?.template_content, template?.template_id]);

  useEffect(() => {
    if (paletteTab === 'queries' && availableQueries.length === 0) {
      loadAvailableQueries();
    }
  }, [paletteTab]);

  const loadAvailableQueries = async () => {
    try {
      setLoadingQueries(true);
      const response = await api.telegramBI.templates.getAvailableQueries();
      setAvailableQueries(response.data?.data || response.data || []);
    } catch (error) {
      console.error('Error loading available queries:', error);
    } finally {
      setLoadingQueries(false);
    }
  };

  const handleChange = useCallback(
    (e) => {
      const val = e.target.value;
      setEditorContent(val);
      setValidationResult(null);
      if (propOnChange) {
        propOnChange(val, template?.template_type || 'text');
      }
    },
    [propOnChange, template?.template_type]
  );

  const handleScroll = useCallback(() => {
    if (highlightRef.current && textareaRef.current) {
      highlightRef.current.scrollTop = textareaRef.current.scrollTop;
      highlightRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
    if (lineNumberRef.current && textareaRef.current) {
      lineNumberRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  }, []);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Tab') {
        e.preventDefault();
        const start = e.target.selectionStart;
        const end = e.target.selectionEnd;
        const val = e.target.value;
        const newVal = val.substring(0, start) + '  ' + val.substring(end);
        setEditorContent(newVal);
        if (propOnChange) propOnChange(newVal, template?.template_type || 'text');
        requestAnimationFrame(() => {
          e.target.selectionStart = e.target.selectionEnd = start + 2;
        });
      }
    },
    [propOnChange, template?.template_type]
  );

  const insertAtCursor = useCallback(
    (text) => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const current = editorContent;
      const newContent = current.substring(0, start) + text + current.substring(end);
      setEditorContent(newContent);
      if (propOnChange) propOnChange(newContent, template?.template_type || 'text');
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + text.length, start + text.length);
      }, 0);
    },
    [editorContent, propOnChange, template?.template_type]
  );

  const insertQueryReference = useCallback(
    (query, type) => {
      let ref;
      if (type === 'inline') {
        ref = `{{query "${query.name}"}}`;
      } else if (type === 'block') {
        ref = `{{#query "${query.name}"}}\n  {{#each this}}\n    {{this.[Field Name]}}\n  {{/each}}\n{{/query}}`;
      } else if (type === 'count') {
        ref = `{{queryCount "${query.name}"}}`;
      }
      insertAtCursor(ref);
    },
    [insertAtCursor]
  );

  const handleValidate = async () => {
    if (!onValidate || !editorContent.trim()) return;
    setIsValidating(true);
    try {
      const result = await onValidate(editorContent, template?.template_type || 'text');
      try {
        const queryValidation =
          await api.telegramBI.templates.validateQueryReferences(editorContent);
        const queryData = queryValidation.data?.data;
        if (queryData && queryData.references && Array.isArray(queryData.references)) {
          result.queryReferences = queryData.references;
        } else if (Array.isArray(queryData)) {
          result.queryReferences = queryData;
        } else {
          result.queryReferences = [];
        }
      } catch {
        result.queryReferences = [];
      }
      setValidationResult(result);
    } catch (err) {
      setValidationResult({
        isValid: false,
        error: err.response?.data?.message || 'Validation failed',
      });
    } finally {
      setIsValidating(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(editorContent);
    toast.success('Copied to clipboard');
  };

  const lineCount = (editorContent || '').split('\n').length;

  const filteredQueries = availableQueries.filter(
    (q) =>
      q.name?.toLowerCase().includes(querySearchTerm.toLowerCase()) ||
      (q.description && q.description.toLowerCase().includes(querySearchTerm.toLowerCase()))
  );

  const renderPalette = () => {
    if (readOnly || !showPalette) return null;

    return (
      <div className="border-t border-gray-700/50 dark:border-gray-200/50 bg-[#1e2127]">
        {/* Palette tabs */}
        <div className="flex items-center gap-0 px-3 border-b border-gray-700/30 dark:border-gray-200/30">
          <button
            onClick={() => setPaletteTab('helpers')}
            className={`px-3 py-1.5 text-[11px] font-medium border-b-2 transition-colors ${
              paletteTab === 'helpers'
                ? 'border-purple-400 text-purple-300 dark:text-purple-800'
                : 'border-transparent text-gray-500 hover:text-gray-300 dark:hover:text-gray-700'
            }`}
          >
            <FaQuestionCircle className="inline mr-1 text-[10px]" />
            Helpers
          </button>
          <button
            onClick={() => setPaletteTab('queries')}
            className={`px-3 py-1.5 text-[11px] font-medium border-b-2 transition-colors ${
              paletteTab === 'queries'
                ? 'border-emerald-400 text-emerald-300 dark:text-emerald-800'
                : 'border-transparent text-gray-500 hover:text-gray-300 dark:hover:text-gray-700'
            }`}
          >
            <FaDatabase className="inline mr-1 text-[10px]" />
            Queries
          </button>
        </div>

        <div className="max-h-52 overflow-y-auto p-2">
          {paletteTab === 'helpers' && (
            <div className="space-y-1">
              {HELPER_CATEGORIES.map((cat) => (
                <div key={cat.name}>
                  <button
                    onClick={() =>
                      setExpandedCategory(expandedCategory === cat.name ? null : cat.name)
                    }
                    className="flex items-center gap-2 w-full px-2 py-1 text-[11px] font-medium text-gray-400 dark:text-gray-600 hover:text-gray-200 dark:hover:text-gray-800 rounded transition-colors"
                  >
                    {expandedCategory === cat.name ? (
                      <FaChevronDown className="text-[8px]" />
                    ) : (
                      <FaChevronRight className="text-[8px]" />
                    )}
                    <span className={cat.color}>{cat.name}</span>
                    <span className="text-gray-600 dark:text-gray-400 text-[10px]">
                      ({cat.helpers.length})
                    </span>
                  </button>
                  {expandedCategory === cat.name && (
                    <div className="grid grid-cols-2 xl:grid-cols-3 gap-1 mt-1 mb-2 pl-4">
                      {cat.helpers.map((h, i) => (
                        <button
                          key={i}
                          onClick={() => insertAtCursor(h.syntax)}
                          className="text-left px-2 py-1.5 rounded bg-gray-800/60 dark:bg-gray-100/60 hover:bg-gray-700/60 dark:hover:bg-gray-200/60 border border-gray-700/40 dark:border-gray-200/40 hover:border-gray-600/50 dark:hover:border-gray-300/50 transition-all group"
                          title={h.desc}
                        >
                          <div className="text-[11px] font-medium text-gray-300 dark:text-gray-700 group-hover:text-white truncate">
                            {h.label}
                          </div>
                          <div className="text-[10px] font-mono text-gray-500 group-hover:text-gray-400 dark:group-hover:text-gray-600 truncate">
                            {h.syntax.split('\n')[0]}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {paletteTab === 'queries' && (
            <div className="space-y-2">
              <div className="relative">
                <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-600 dark:text-gray-400 text-[10px]" />
                <input
                  type="text"
                  placeholder="Search queries..."
                  value={querySearchTerm}
                  onChange={(e) => setQuerySearchTerm(e.target.value)}
                  className="w-full pl-7 pr-2 py-1.5 text-[11px] bg-gray-800/60 dark:bg-gray-100/60 border border-gray-700/40 dark:border-gray-200/40 rounded text-gray-300 dark:text-gray-700 placeholder-gray-600 dark:placeholder-gray-400 outline-none focus:border-gray-500 dark:focus:border-gray-400"
                />
              </div>
              {loadingQueries ? (
                <div className="flex items-center justify-center py-4">
                  <div className="w-4 h-4 border-2 border-gray-600 dark:border-gray-300 border-t-emerald-400 rounded-full animate-spin" />
                </div>
              ) : filteredQueries.length === 0 ? (
                <div className="text-[11px] text-gray-500 text-center py-3">
                  {querySearchTerm ? 'No queries match' : 'No queries available'}
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredQueries.map((q) => (
                    <div
                      key={q.id}
                      className="flex items-center justify-between px-2 py-1.5 rounded bg-gray-800/40 dark:bg-gray-100/40 border border-gray-700/30 dark:border-gray-200/30"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-[11px] font-medium text-gray-300 dark:text-gray-700 truncate">
                          {q.name}
                        </div>
                        {q.description && (
                          <div className="text-[10px] text-gray-500 truncate">{q.description}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                        <button
                          onClick={() => insertQueryReference(q, 'inline')}
                          className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-emerald-900/40 dark:bg-emerald-100/40 text-emerald-400 hover:bg-emerald-800/50 dark:hover:bg-emerald-200/50 transition-colors"
                        >
                          Inline
                        </button>
                        <button
                          onClick={() => insertQueryReference(q, 'block')}
                          className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-emerald-900/40 dark:bg-emerald-100/40 text-emerald-400 hover:bg-emerald-800/50 dark:hover:bg-emerald-200/50 transition-colors"
                        >
                          Block
                        </button>
                        <button
                          onClick={() => insertQueryReference(q, 'count')}
                          className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-emerald-900/40 dark:bg-emerald-100/40 text-emerald-400 hover:bg-emerald-800/50 dark:hover:bg-emerald-200/50 transition-colors"
                        >
                          #
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Sidebar mode for modals (CreateTemplateModal / EditTemplateModal)
  if (sidebarMode) {
    return (
      <div className="flex h-full gap-4">
        <div className="flex-1 flex flex-col min-w-0">
          <div
            className={`rounded-lg overflow-hidden border border-gray-700/50 dark:border-gray-200/50 flex flex-col flex-1 ${isExpanded ? 'fixed inset-4 z-50' : ''}`}
          >
            {/* Toolbar */}
            <div className="flex items-center justify-between px-3 py-1.5 bg-[#21252b] border-b border-gray-700/50 dark:border-gray-200/50">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
                  <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
                  <span className="w-3 h-3 rounded-full bg-[#28c840]" />
                </div>
                <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wider ml-2">
                  HBS
                </span>
                {!readOnly && (
                  <span className="text-[11px] text-gray-600 dark:text-gray-400">
                    {lineCount} line{lineCount !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {!readOnly && onValidate && (
                  <button
                    onClick={handleValidate}
                    disabled={isValidating || !editorContent.trim()}
                    className="flex items-center gap-1.5 px-2 py-1 rounded text-[11px] font-medium text-gray-400 dark:text-gray-600 hover:text-gray-200 dark:hover:text-gray-800 hover:bg-surface/5 disabled:opacity-40 transition-colors"
                  >
                    {isValidating ? (
                      <span className="w-3 h-3 border border-gray-500 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <FaCheck className="text-[10px]" />
                    )}
                    Validate
                  </button>
                )}
                <button
                  onClick={handleCopy}
                  className="p-1.5 rounded text-gray-500 hover:text-gray-300 dark:hover:text-gray-700 hover:bg-surface/5 transition-colors"
                  title="Copy"
                >
                  <FaCopy className="text-[11px]" />
                </button>
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="p-1.5 rounded text-gray-500 hover:text-gray-300 dark:hover:text-gray-700 hover:bg-surface/5 transition-colors"
                  title={isExpanded ? 'Collapse' : 'Expand'}
                >
                  {isExpanded ? (
                    <FaCompress className="text-[11px]" />
                  ) : (
                    <FaExpand className="text-[11px]" />
                  )}
                </button>
              </div>
            </div>

            {/* Editor body */}
            <div className={`relative bg-[#282c34] ${isExpanded ? 'flex-1' : 'h-80'}`}>
              {/* Line numbers */}
              <div
                ref={lineNumberRef}
                className="absolute left-0 top-0 bottom-0 w-10 bg-[#282c34] border-r border-gray-700/30 dark:border-gray-200/30 overflow-hidden pointer-events-none z-10"
              >
                <div className="pt-3 px-1.5 text-right">
                  {Array.from({ length: lineCount }, (_, i) => (
                    <div
                      key={i}
                      className="text-[11px] leading-5 text-gray-600 dark:text-gray-400 select-none font-mono"
                    >
                      {i + 1}
                    </div>
                  ))}
                </div>
              </div>

              {/* Highlight layer */}
              <pre
                ref={highlightRef}
                className="absolute inset-0 pl-12 pt-3 pr-3 pb-3 overflow-auto font-mono text-[13px] leading-5 text-[#abb2bf] whitespace-pre-wrap break-words pointer-events-none"
                aria-hidden="true"
                dangerouslySetInnerHTML={{ __html: highlightHandlebars(editorContent) + '\n' }}
              />

              {/* Textarea */}
              <textarea
                ref={textareaRef}
                id="template-content"
                value={editorContent}
                onChange={handleChange}
                onScroll={handleScroll}
                onKeyDown={handleKeyDown}
                readOnly={readOnly}
                className="absolute inset-0 pl-12 pt-3 pr-3 pb-3 w-full h-full resize-none bg-transparent text-transparent caret-[#528bff] font-mono text-[13px] leading-5 outline-none selection:bg-[#3e4451]"
                spellCheck={false}
                placeholder={readOnly ? '' : 'Write your Handlebars template...'}
                style={{ caretColor: '#528bff' }}
              />
            </div>

            {/* Validation result */}
            {validationResult && (
              <div
                className={`px-3 py-2 border-t text-xs flex items-start gap-2 ${
                  validationResult.isValid
                    ? 'bg-[#1e3a2f] border-emerald-800/50 dark:border-emerald-300/50 text-emerald-400'
                    : 'bg-[#3a1e1e] border-red-800/50 dark:border-red-300/50 text-red-400'
                }`}
              >
                {validationResult.isValid ? (
                  <FaCheck className="text-[10px] mt-0.5 flex-shrink-0" />
                ) : (
                  <FaExclamationTriangle className="text-[10px] mt-0.5 flex-shrink-0" />
                )}
                <div>
                  <span>
                    {validationResult.isValid
                      ? 'Template is valid'
                      : validationResult.error || 'Template has errors'}
                  </span>
                  {validationResult.queryReferences &&
                    validationResult.queryReferences.length > 0 && (
                      <div className="mt-1.5 space-y-0.5">
                        {validationResult.queryReferences.map((ref, i) => (
                          <div
                            key={i}
                            className={`flex items-center gap-1 text-[10px] ${ref.resolved ? 'text-emerald-400/80' : 'text-red-400/80'}`}
                          >
                            {ref.resolved ? (
                              <FaCheck className="text-[8px]" />
                            ) : (
                              <FaTimes className="text-[8px]" />
                            )}
                            <span>
                              {ref.type}: "{ref.identifier}"
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                </div>
              </div>
            )}

            {/* Helper palette */}
            {renderPalette()}

            {isExpanded && (
              <div
                className="fixed inset-0 bg-black/60 -z-10"
                onClick={() => setIsExpanded(false)}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  // Default non-sidebar mode (used in index.js detail view)
  return (
    <div
      className={`overflow-hidden ${isExpanded ? 'fixed inset-4 z-50 flex flex-col rounded-lg border border-gray-700/50 dark:border-gray-200/50' : ''}`}
    >
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-[#21252b] border-b border-gray-700/50 dark:border-gray-200/50">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
            <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
            <span className="w-3 h-3 rounded-full bg-[#28c840]" />
          </div>
          <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wider ml-2">
            HBS
          </span>
          <span className="text-[11px] text-gray-600 dark:text-gray-400">
            {lineCount} line{lineCount !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {!readOnly && onValidate && (
            <button
              onClick={handleValidate}
              disabled={isValidating || !editorContent.trim()}
              className="flex items-center gap-1.5 px-2 py-1 rounded text-[11px] font-medium text-gray-400 dark:text-gray-600 hover:text-gray-200 dark:hover:text-gray-800 hover:bg-surface/5 disabled:opacity-40 transition-colors"
            >
              {isValidating ? (
                <span className="w-3 h-3 border border-gray-500 border-t-transparent rounded-full animate-spin" />
              ) : (
                <FaCheck className="text-[10px]" />
              )}
              Validate
            </button>
          )}
          <button
            onClick={handleCopy}
            className="p-1.5 rounded text-gray-500 hover:text-gray-300 dark:hover:text-gray-700 hover:bg-surface/5 transition-colors"
            title="Copy"
          >
            <FaCopy className="text-[11px]" />
          </button>
          {!readOnly && (
            <button
              onClick={() => setShowPalette(!showPalette)}
              className={`p-1.5 rounded transition-colors ${showPalette ? 'text-purple-400 bg-surface/5' : 'text-gray-500 hover:text-gray-300 dark:hover:text-gray-700 hover:bg-surface/5'}`}
              title="Toggle helper palette"
            >
              <FaCode className="text-[11px]" />
            </button>
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1.5 rounded text-gray-500 hover:text-gray-300 dark:hover:text-gray-700 hover:bg-surface/5 transition-colors"
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? (
              <FaCompress className="text-[11px]" />
            ) : (
              <FaExpand className="text-[11px]" />
            )}
          </button>
        </div>
      </div>

      {/* Editor */}
      <div className={`relative bg-[#282c34] ${isExpanded ? 'flex-1' : 'h-64'}`}>
        {/* Line numbers */}
        <div
          ref={lineNumberRef}
          className="absolute left-0 top-0 bottom-0 w-10 bg-[#282c34] border-r border-gray-700/30 dark:border-gray-200/30 overflow-hidden pointer-events-none z-10"
        >
          <div className="pt-3 px-1.5 text-right">
            {Array.from({ length: lineCount }, (_, i) => (
              <div
                key={i}
                className="text-[11px] leading-5 text-gray-600 dark:text-gray-400 select-none font-mono"
              >
                {i + 1}
              </div>
            ))}
          </div>
        </div>

        {/* Highlight layer */}
        <pre
          ref={highlightRef}
          className="absolute inset-0 pl-12 pt-3 pr-3 pb-3 overflow-auto font-mono text-[13px] leading-5 text-[#abb2bf] whitespace-pre-wrap break-words pointer-events-none"
          aria-hidden="true"
          dangerouslySetInnerHTML={{ __html: highlightHandlebars(editorContent) + '\n' }}
        />

        {/* Textarea */}
        <textarea
          ref={textareaRef}
          id="template-content"
          value={editorContent}
          onChange={handleChange}
          onScroll={handleScroll}
          onKeyDown={handleKeyDown}
          readOnly={readOnly}
          className="absolute inset-0 pl-12 pt-3 pr-3 pb-3 w-full h-full resize-none bg-transparent text-transparent caret-[#528bff] font-mono text-[13px] leading-5 outline-none selection:bg-[#3e4451]"
          spellCheck={false}
          placeholder={readOnly ? '' : 'Write your Handlebars template...'}
          style={{ caretColor: '#528bff' }}
        />
      </div>

      {/* Validation result */}
      {validationResult && (
        <div
          className={`px-3 py-2 border-t text-xs flex items-start gap-2 ${
            validationResult.isValid
              ? 'bg-[#1e3a2f] border-emerald-800/50 text-emerald-400'
              : 'bg-[#3a1e1e] border-red-800/50 text-red-400'
          }`}
        >
          {validationResult.isValid ? (
            <FaCheck className="text-[10px] mt-0.5 flex-shrink-0" />
          ) : (
            <FaExclamationTriangle className="text-[10px] mt-0.5 flex-shrink-0" />
          )}
          <div>
            <span>
              {validationResult.isValid
                ? 'Template is valid'
                : validationResult.error || 'Template has errors'}
            </span>
            {validationResult.queryReferences && validationResult.queryReferences.length > 0 && (
              <div className="mt-1.5 space-y-0.5">
                {validationResult.queryReferences.map((ref, i) => (
                  <div
                    key={i}
                    className={`flex items-center gap-1 text-[10px] ${ref.resolved ? 'text-emerald-400/80' : 'text-red-400/80'}`}
                  >
                    {ref.resolved ? (
                      <FaCheck className="text-[8px]" />
                    ) : (
                      <FaTimes className="text-[8px]" />
                    )}
                    <span>
                      {ref.type}: "{ref.identifier}"
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Helper palette */}
      {renderPalette()}

      {isExpanded && (
        <div className="fixed inset-0 bg-black/60 -z-10" onClick={() => setIsExpanded(false)} />
      )}
    </div>
  );
};

export default TemplateEditor;
